import { useEffect, useRef, useState } from 'react';
import type { DrawAction, DrawCommand } from '../../../shared/types';
import { useAmDrawer, useGameStore } from '../store/gameStore';
import type { GameSocketApi } from '../hooks/useGameSocket';

/** Friendly nicknames for the default palette. */
export const PALETTE = [
  '#000000',
  '#ef4444',
  '#f59e0b',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#ffffff',
];

const DEFAULT_COLOR = '#000000';
const DEFAULT_SIZE = 5;
const LOGICAL_W = 800;
const LOGICAL_H = 600;
/** Between the browser painting rows and node bounds, commands stream in here. */
const FLUSH_MS = 24;

interface DrawCanvasProps {
  socket: GameSocketApi;
}

/** Persistent paint context so we can resume mid-stroke on incremental batches. */
interface RenderState {
  open: { color: string; size: number } | null;
  last: { x: number; y: number };
}

function freshState(): RenderState {
  return { open: null, last: { x: 0, y: 0 } };
}

/**
 * The shared canvas. In "drawer" mode the player can paint; strokes are
 * normalized (0..1), batched, and broadcast as DrawCommands while being
 * painted locally. In "guesser" mode the canvas is read-only and replays the
 * incoming command stream. Rendering is incremental: only the commands we
 * haven't drawn yet get painted, so updates stay realtime even with thousands
 * of points on the board.
 */
export default function DrawCanvas({ socket }: DrawCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawer = useAmDrawer();
  const drawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const colorRef = useRef(DEFAULT_COLOR);
  const sizeRef = useRef(DEFAULT_SIZE);
  const socketRef = useRef(socket);
  socketRef.current = socket;

  // Outbound batching: fold many pointer events into a couple of messages.
  const queueRef = useRef<DrawCommand[]>([]);
  // Index of the next command already painted on the canvas.
  const paintedRef = useRef(0);
  const renderStateRef = useRef<RenderState>(freshState());

  const [color, setColor] = useState(DEFAULT_COLOR);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [hasContent, setHasContent] = useState(false);

  // ── Incremental renderer (subscribes to the command stream) ─────────────

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    canvas.width = LOGICAL_W;
    canvas.height = LOGICAL_H;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);

    const paintWindow = (cmds: DrawCommand[], from: number, to: number) => {
      for (let i = from; i < to && i < cmds.length; i++) {
        const cmd = cmds[i];
        if (cmd.type === 'CLEAR') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);
          renderStateRef.current = freshState();
          continue;
        }
        const a = cmd.action;
        if (!a) continue;
        const st = renderStateRef.current;
        const x = a.x * LOGICAL_W;
        const y = a.y * LOGICAL_H;

        if (a.kind === 'begin') {
          st.open = { color: a.color, size: a.size };
          ctx.strokeStyle = a.color;
          ctx.lineWidth = a.size;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(x, y);
          st.last = a;
        } else if (a.kind === 'point') {
          if (!st.open || st.open.color !== a.color || st.open.size !== a.size) {
            // A point streamed without its begin (tool change) — reconnect.
            st.open = { color: a.color, size: a.size };
            ctx.strokeStyle = a.color;
            ctx.lineWidth = a.size;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(st.last.x * LOGICAL_W, st.last.y * LOGICAL_H);
          }
          ctx.lineTo(x, y);
          ctx.stroke();
          st.last = a;
        } else if (a.kind === 'end') {
          if (st.open) {
            ctx.lineTo(x, y);
            ctx.stroke();
          }
          st.open = null;
          st.last = a;
        }
      }
    };

    const fullRepaint = (cmds: DrawCommand[]) => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);
      renderStateRef.current = freshState();
      paintWindow(cmds, 0, cmds.length);
      paintedRef.current = cmds.length;
    };

    // Paint whatever was already buffered on mount. fullRepaint resets the
    // painted cursor so StrictMode's double-mounted effect stays consistent.
    const initial = useGameStore.getState().snapshot?.drawCommands ?? [];
    fullRepaint(initial);
    if (initial.length > 0) setHasContent(true);
    // A late join may still be mid-stroke in the window; that's handled below.

    const applyNew = (next: DrawCommand[], prevCount: number) => {
      if (next.length >= paintedRef.current) {
        // Normal append → paint only the delta.
        paintWindow(next, paintedRef.current, next.length);
        paintedRef.current = next.length;
        if (next.length > 0) setHasContent(true);
      } else if (next.length < paintedRef.current || prevCount > next.length) {
        // Stream was reset (new round / new snapshot) → full repaint.
        fullRepaint(next);
        setHasContent(next.length > 0);
      }
    };

    const unsubscribe = useGameStore.subscribe((s, prev) => {
      const next = s.snapshot?.drawCommands ?? [];
      const prevLen = prev.snapshot?.drawCommands?.length ?? 0;
      if (prevLen !== next.length) applyNew(next, prevLen);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Outbound batcher ─────────────────────────────────────────────────────

  const flush = () => {
    if (queueRef.current.length === 0) return;
    const chunk = queueRef.current;
    queueRef.current = [];
    useGameStore.getState().appendDraw(chunk);
    socketRef.current.sendDraw(chunk);
  };

  useEffect(() => {
    const id = window.setInterval(flush, FLUSH_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => flush(), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // ── Pointer events (drawer only) ─────────────────────────────────────────

  const toNormalized = (e: PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return { x: -1, y: -1 };
    }
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const stroke = (action: DrawAction) => {
    queueRef.current.push({ type: 'ACTION', action });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawer) return;
    e.preventDefault();
    canvasRef.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    const p = toNormalized(e.nativeEvent);
    if (p.x < 0) return;
    lastPoint.current = p;
    stroke({ kind: 'begin', x: p.x, y: p.y, color: colorRef.current, size: sizeRef.current });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawer || !drawing.current) return;
    e.preventDefault();
    const p = toNormalized(e.nativeEvent);
    if (p.x < 0) return;
    const last = lastPoint.current;
    if (last && Math.hypot(p.x - last.x, p.y - last.y) < 0.0015) return; // skip micro-jitter
    lastPoint.current = p;
    stroke({ kind: 'point', x: p.x, y: p.y, color: colorRef.current, size: sizeRef.current });
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawer || !drawing.current) return;
    drawing.current = false;
    const p = toNormalized(e.nativeEvent);
    const prev = lastPoint.current;
    lastPoint.current = null;
    const ex = p.x >= 0 ? p.x : prev?.x ?? 0.5;
    const ey = p.y >= 0 ? p.y : prev?.y ?? 0.5;
    stroke({ kind: 'end', x: ex, y: ey, color: colorRef.current, size: sizeRef.current });
    flush(); // drop the final batch immediately — no trailing lag
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden">
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl bg-white shadow-inner ring-1 ring-scr-border">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          className={`h-full w-full touch-none ${isDrawer ? 'cursor-crosshair' : 'cursor-default'}`}
        />
        {!isDrawer && (
          <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/40 px-3 py-1 text-[11px] font-semibold text-white">
            Watch-only
          </div>
        )}
        {!hasContent && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-medium text-scr-ink/25">
            {isDrawer ? 'Draw here' : 'Waiting for the artist…'}
          </div>
        )}
      </div>

      {/* Toolbar — only visible for the active drawer */}
      <div
        className={`flex flex-wrap items-center gap-2 p-2 transition-opacity ${
          isDrawer ? 'opacity-100' : 'pointer-events-none h-0 p-0 opacity-0'
        }`}
      >
        {PALETTE.map((c) => (
          <button
            key={c}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => {
              colorRef.current = c;
              setColor(c);
            }}
            aria-label={`Color ${c}`}
            className={`h-7 w-7 rounded-full border-2 transition ${
              color === c ? 'scale-110 border-scr-accent' : 'border-black/10 hover:scale-105'
            }`}
            style={{ backgroundColor: c }}
          />
        ))}

        <div className="mx-1 h-6 w-px bg-scr-border" />

        {[3, 5, 9, 16].map((s) => (
          <button
            key={s}
            onClick={() => {
              sizeRef.current = s;
              setSize(s);
            }}
            aria-label={`Brush size ${s}`}
            className={`flex h-8 w-8 items-center justify-center rounded-full border transition ${
              size === s ? 'border-scr-accent bg-scr-accent/10' : 'border-black/10 hover:bg-black/5'
            }`}
          >
            <span
              className="rounded-full bg-scr-ink"
              style={{ width: s / 2 + 3, height: s / 2 + 3 }}
            />
          </button>
        ))}

        <button
          onClick={() => {
            flush(); // push any pending strokes out first
            useGameStore.getState().appendDraw([{ type: 'CLEAR' }]);
            socketRef.current.clearCanvas();
          }}
          className="ml-auto rounded-full border border-black/10 px-3 py-1.5 text-xs font-semibold text-scr-ink/60 transition hover:bg-red-50 hover:text-red-600"
        >
          🗑 Clear
        </button>
      </div>
    </div>
  );
}